import React, { useState } from "react";
import { IUserV1 } from "../../../api/Account/usersApi";
import { Badge, Link, Checkbox, Box } from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridToolbar,
} from "@mui/x-data-grid";
import { useDataGridStyles } from "../../../hooks/useDataGridStyles";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import NotInterestedIcon from "@mui/icons-material/NotInterested";
import PhoneIcon from "@mui/icons-material/Phone";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { styled } from "@mui/material/styles";

export interface IUsersGridProps {
  users: IUserV1[];
  selectionChanged: (user: IUserV1 | undefined) => void;
}

declare type fontColor =
  | "inherit"
  | "disabled"
  | "action"
  | "primary"
  | "secondary"
  | "error"
  | undefined;

declare type textColor =
  | "initial"
  | "inherit"
  | "primary"
  | "secondary"
  | "textPrimary"
  | "textSecondary"
  | "error";

const UsersGrid: React.FC<IUsersGridProps> = ({ users, selectionChanged }) => {
  const [selectedUser, setSelectedUser] = useState<IUserV1 | undefined>();
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });

  const { styles: gridStyles } = useDataGridStyles({
    variant: "common",
    type: "users",
  });

  const StyledLink = styled(Link)({
    padding: "2px 4px 2px 10px",
  });

  function getDisplayColor(user: IUserV1 | undefined): fontColor {
    if (user?.isExternal === true) {
      return "primary";
    }
    const color: fontColor =
      user?.syncStatus !== 0
        ? "error"
        : user?.isTestUser
        ? "disabled"
        : "inherit";
    return color;
  }

  function getTextColor(user: IUserV1 | undefined): textColor {
    if (user?.isExternal === true) {
      return "textPrimary";
    }
    const color: textColor =
      user?.syncStatus !== 0
        ? "error"
        : user?.isTestUser
        ? "textSecondary"
        : "inherit";
    return color;
  }

  function renderUserIcon(user: IUserV1 | undefined) {
    const color = getDisplayColor(user);
    if (user?.isExternal === true) {
      return <PersonOutlineIcon color={color} />;
    } else if (user?.isAdmin === true) {
      return <SupervisorAccountIcon color={color} />;
    } else if (user?.isTestUser === true) {
      return <NotInterestedIcon color={color} />;
    } else if (user?.isCompany === true) {
      return <BusinessIcon color={color} />;
    } else if (user?.phoneNumber) {
      return <PhoneIcon color={color} />;
    } else if (user?.isCustomer === true) {
      return <PersonIcon color={color} />;
    }
    return null;
  }

  const handleRowClick = (params: { row: IUserV1 }) => {
    const user = params.row as IUserV1;
    setSelectedUser(user);
    selectionChanged(user);
  };

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Name",
      width: 300,
      renderCell: (params: GridRenderCellParams) => {
        const user = params.row as IUserV1;
        return (
          <Box display="flex" alignItems="center" gap={1}>
            <Badge
              color="error"
              variant="dot"
              invisible={user?.syncStatus === 0}
            >
              {renderUserIcon(user)}
            </Badge>
            <StyledLink
              color={getTextColor(user)}
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleRowClick(params);
              }}
            >
              {user?.name}
            </StyledLink>
          </Box>
        );
      },
    },
    {
      field: "companyName",
      headerName: "Company Name",
      width: 250,
    },
    {
      field: "accountType",
      headerName: "Account Type",
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        const user = params.row as IUserV1;
        return user.isAdmin ? "Admin" : user.isCustomer ? "Customer" : "User";
      },
    },
    {
      field: "isActive",
      headerName: "Active",
      width: 100,
      renderCell: (params: GridRenderCellParams) => {
        const user = params.row as IUserV1;
        return <Checkbox checked={user?.isActive || false} readOnly />;
      },
    },
  ];

  return (
    <div style={{ height: "80vh", width: "100%" }}>
      <DataGrid
        rows={users}
        columns={columns}
        getRowId={(row) => row.userId}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 100 },
          },
          sorting: {
            sortModel: [{ field: "name", sort: "asc" }],
          },
        }}
        sx={{
          ...gridStyles,
          "& .MuiDataGrid-row": {
            cursor: "pointer",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "action.hover",
          },
          [`& .MuiDataGrid-row[data-id="${selectedUser?.userId}"]`]: {
            backgroundColor: "action.selected",
          },
        }}
        rowHeight={56}
        slots={{ toolbar: GridToolbar }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            quickFilterProps: { debounceMs: 500 },
          },
        }}
        pageSizeOptions={[25, 50, 100]}
        disableRowSelectionOnClick
        onRowClick={handleRowClick}
      />
    </div>
  );
};

export default UsersGrid;
